import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

const safeNumber = (value, defaultValue = 0) => {
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  const parsed = parseFloat(value);
  return !isNaN(parsed) ? parsed : defaultValue;
};

const formatPercentage = (value) => {
  const num = safeNumber(value);
  return Math.round(num);
};

const generateNormalCurve = (mean = 50, stdDev = 15, points = 100) => {
  const curve = [];
  const minX = 0;
  const maxX = 100;
  const step = (maxX - minX) / points;

  for (let i = 0; i <= points; i++) {
    const x = minX + (i * step);
    const y = (1 / (stdDev * Math.sqrt(2 * Math.PI))) *
              Math.exp(-0.5 * Math.pow((x - mean) / stdDev, 2));
    curve.push({ x, y });
  }

  const maxY = Math.max(...curve.map(point => point.y));
  return curve.map(point => ({
    x: point.x,
    y: (point.y / maxY) * 80 // Scale to 80% of chart height
  }));
};

/**
 * PersistenceSection - Specialized component for displaying persistence construct data
 *
 * This component provides a unique UI for the Persistence construct, featuring:
 * - Left side: Normal distribution (bell curve) with percentile indicator line
 * - Right side: Personalized passages with insights based on the user's percentile position
 *
 * The component safely handles data extraction from Redux contextData and provides
 * fallback values for missing or invalid data.
 *
 * @returns {JSX.Element} The persistence dashboard with bell curve and personalized content
 */
const PersistenceSection = () => {
  const { contextData } = useSelector((state) => state.studentPerformenceConstruct);

  const persistenceData = useMemo(() => {
    if (!contextData) return null;

    const { construct_label = [], behaviors = {} } = contextData;

    const persistenceLabel = construct_label.find(item =>
      item.behavior_graph_key?.toLowerCase().includes('persistence') ||
      item.label?.toLowerCase().includes('persistence')
    );

    if (!persistenceLabel || typeof persistenceLabel.percentage === 'undefined') {
      return null; 
    }

    const rawPercentile = persistenceLabel.percentage;

    const percentile = Math.max(0, Math.min(100, safeNumber(rawPercentile)));

    const features = {};
    if (behaviors && typeof behaviors === 'object') {
      Object.entries(behaviors).forEach(([key, value]) => {
        if (key.toLowerCase().includes('persistence')) {
          features[key] = {
            percentage: safeNumber(value?.percentage || value),
            raw_value: value?.raw_value || value?.value || value,
            human_readable_name: value?.human_readable_name || key.replace(/_/g, ' ')
          };
        }
      });
    }

    return {
      percentile,
      features,
      rawData: persistenceLabel
    };
  }, [contextData]);

  const getPassageContent = (percentile) => {
    if (percentile >= 67) {
      return {
        category: 'High Persistence',
        color: '#22c55e',
        passage: `You exhibit high levels of persistence, demonstrating a remarkable ability to push through challenges even after multiple failures. Rather than quitting when faced with difficulties, you keep trying different strategies and persist until you achieve success. Your perseverance is particularly evident in your willingness to retry levels multiple times, constantly refining your approach to overcome obstacles. You do not let setbacks deter you; instead, you use them as opportunities to improve. With your strong determination, you are well-equipped to handle difficult tasks and keep progressing toward your goals, making steady progress no matter how challenging the situation may seem.`
      };
    } else if (percentile >= 33) {
      return {
        category: 'Mid Persistence',
        color: '#3b82f6',
        passage: `On Circuit Board, You demonstrate a moderate level of persistence, showing a willingness to engage with challenges and retry tasks after experiencing failure. While you do tend to take breaks or adjust your approach when faced with obstacles, you typically do not quit outright. 
        
        Your persistence allows you to handle moderate challenges, though there may be times when you feel the need to step back and reconsider your strategies. With a bit more consistency in pushing through difficulties without shifting focus too often, you could increase your perseverance, leading to even greater success in challenging tasks.`
      };
    } else {
      return {
        category: 'Low Persistence',
        color: '#f59e0b',
        passage: `You exhibit lower persistence levels, often giving up or taking breaks when faced with difficult tasks or setbacks. Instead of pushing through challenges, you tend to disengage and move on to other tasks or quit entirely. This tendency to avoid retrying after failure may limit your progress, as you are more likely to seek out easier tasks or require external help to maintain your focus. Building resilience and practicing persistence through repeated attempts at difficult tasks could help you increase your overall engagement and success in challenging situations.`
      };
    }
  };

  if (!persistenceData) {
    return (
      <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading persistence data...</p>
        </div>
      </div>
    );
  }

  const { percentile, features } = persistenceData;
  const passageInfo = getPassageContent(percentile);
  const curvePoints = generateNormalCurve();

  const createCurvePath = (points) => {
    if (points.length === 0) return '';

    let path = `M ${points[0].x} ${100 - points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      path += ` L ${points[i].x} ${100 - points[i].y}`;
    }
    return path;
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col md:flex-row gap-6">
      {/* Left side - Normal Distribution Chart */}
      <div className="w-full md:w-1/2 bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <h3 className="text-lg sm:text-xl md:!text-2xl font-semibold text-black mb-3 sm:!mb-4 text-center">Persistence Distribution</h3>

        {/* Chart Container */}
        <div className="relative">
          {/* SVG Bell Curve */}
          <div className="relative h-80 border-2 border-gray-400 bg-white">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              {/* Bell curve */}
              <path
                d={createCurvePath(curvePoints)}
                fill="rgba(59, 130, 246, 0.2)"
                stroke="rgba(59, 130, 246, 0.8)"
                strokeWidth="0.5"
              />

              {/* Percentile line */}
              <line
                x1={percentile}
                y1="20"
                x2={percentile}
                y2="100"
                stroke="red"
                strokeWidth="0.8"
                strokeDasharray="2,2"
              />
            </svg>

            {/* X-axis labels */}
            <div className="absolute -bottom-1 left-0 w-full flex justify-between text-xs sm:text-sm md:text-lg text-gray-600 transform translate-y-6">
              <span>0</span>
              <span>25</span>
              <span>50</span>
              <span>75</span>
              <span>100</span>
            </div>

            {/* Percentile label positioned above the red line */}
            <div
              className="absolute top-4 transform -translate-x-1/2"
              style={{ left: `${percentile}%` }}
            >
              <div className="text-xs sm:text-sm md:text-lg text-red-600 font-bold bg-white px-1 rounded shadow-sm">
                {formatPercentage(percentile)}%
              </div>
            </div>
          </div>

          {/* X-axis label */}
          <div className="text-center mt-8">
            <div className="text-xs sm:text-sm md:text-lg font-medium text-gray-700">
              Persistence Percentile
            </div>
          </div>
        </div>

        {/* Raw Values Display */}
        <div className="mt-6">
          <div className="bg-blue-50 p-3 rounded text-center">
            <div className="text-sm sm:text-lg font-medium text-gray-600 text-nowrap">Your Persistence Score</div>
            <div className="text-lg sm:text-2xl font-bold text-blue-600">
              {formatPercentage(percentile)}th percentile
            </div>

          </div>
        </div>

        {/* Percentile Explanation */}
        <div className="mt-4 sm:mt-6  w-full p-3 bg-[#0000000A] rounded-lg border border-gray-200">
          <p className="text-xs sm:text-sm md:text-md text-gray-700 leading-relaxed">
            <strong>About Percentiles:</strong> Percentiles show how your score compares to a nationally representative sample of U.S. adults. For example, if you score in the 75th percentile, you scored higher than 75% of adults in the U.S.
          </p>
        </div>
      </div>

      {/* Right side - Personalized Passage */}
      <div className="w-full md:w-1/2 bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center mb-4">
          <div className="w-4 h-4 rounded mr-3" style={{ backgroundColor: passageInfo.color }}></div>
          <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-black">{passageInfo.category}</h3>
        </div>

        <div className="prose prose-sm max-w-none h-full flex flex-col justify-start">
          <div className="space-y-8">
            {passageInfo.passage.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-xl break-words">
                {paragraph}
              </p>
            ))}
          </div>
        </div>



      </div>
      </div>
    </div>
  );
};

export default PersistenceSection;
